import { DateTime } from 'luxon'

import {
  DateTimeUnits,
  type DateTimeUnitsType,
  type DateTimeShortUnitsType,
  type DateTimeDurationType
} from '@models/entities/dates'

export const UTC_TIMEZONE = 'utc'
export const DEFAULT_DATE_SEPARATOR = '.'
export const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy'
export const API_DATE_FORMAT = 'yyyy-MM-dd'
export const DEFAULT_DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm:ss'
export const DATE_TIME_UNITS: DateTimeUnitsType[] = [
  DateTimeUnits.SECONDS,
  DateTimeUnits.MINUTES,
  DateTimeUnits.HOURS,
  DateTimeUnits.DAYS
]

export const isLuxonDate = (date: any) => date instanceof DateTime
export const getNowUtcDate = () => DateTime.utc()
export const getDateObjFromJSDate = (date: Date) => DateTime.fromJSDate(date)
export const getDateObjFromIso = (isoDate: string, zone?: string) =>
  DateTime.fromISO(isoDate, zone ? { zone: zone } : { setZone: true })
export const getDateObjFromFormat = (date: string, format = DEFAULT_DATE_TIME_FORMAT) =>
  DateTime.fromFormat(date, format)
export const getDateObjFromObj = (obj: DateTimeDurationType) => DateTime.fromObject(obj)
export const formatDateToJSDate = (date: any) => isLuxonDate(date) ? date.toJSDate() : date
export const formatDate = (date: any, format: string = DEFAULT_DATE_TIME_FORMAT) =>
  isLuxonDate(date) ? date.toFormat(format) : date
export const dateMinusDuration = (date: any, duration: DateTimeDurationType) =>
  isLuxonDate(date) ? date.minus(duration) : date
export const datePlusDuration = (date: any, duration: DateTimeDurationType) =>
  isLuxonDate(date) ? date.plus(duration) : date
export const getDateDiff = (
  endDate: any,
  startDate: any
) => isLuxonDate(endDate) && isLuxonDate(startDate) ? endDate.diff(startDate) : endDate
export const getDatesDifference = (
  endDate: any,
  startDate: any,
  units: DateTimeUnitsType | DateTimeUnitsType[] = DATE_TIME_UNITS
) => endDate.diff(startDate, units)
export const getDatesDifferenceSimpleObj = (
  endDate: any,
  startDate: any,
  units: DateTimeUnitsType | DateTimeUnitsType[] = DATE_TIME_UNITS
) => getDatesDifference(endDate, startDate, units).toObject()
export const getDateStartOfUnit = (date: any, unit: DateTimeShortUnitsType = DateTimeUnits.DAY) =>
  isLuxonDate(date) ? date.startOf(unit) : date
export const getDateEndOfUnit = (date: any, unit: DateTimeShortUnitsType = DateTimeUnits.DAY) =>
  isLuxonDate(date) ? date.endOf(unit) : date

export const createNativeDate = (days: number, months: number, years: number): Date => {
  const date: Date = new Date()

  date.setDate(date.getDate() + days)
  date.setMonth(date.getMonth() + months)
  date.setFullYear(date.getFullYear() + years)

  return date
}

export const getDefaultMinDate = () => new Date('01 Jan 1900')
export const getDefaultMaxDate = () => new Date()

export const getTodayDateRange = (): Date[] => {
  const today = new Date()
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  return [startOfDay, today]
}

export const getThisWeekDateRange = (): Date[] => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const startOfWeek = new Date(today)

  startOfWeek.setDate(today.getDate() + diff)
  startOfWeek.setHours(0, 0, 0, 0)

  return [startOfWeek, today]
}

export const getThisMonthDateRange = (): Date[] => {
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  return [startOfMonth, today]
}

export const getThisYearDateRange = (): Date[] => {
  const today = new Date()
  const startOfYear = new Date(today.getFullYear(), 0, 1)

  return [startOfYear, today]
}
